"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _svgosm = require("./svgosm.js");
const fetchCache = new Map();

/**
 * Fetches an icon from the CDN and returns the icon content.
 * It expects the svg icon to be available in the CDN at `${options.cdn}/${icon}.svg`.
 *
 * @param {string} icon - The name of the icon to fetch.
 * @param {string} options.cdn - The base URL of the CDN.
 * @param {Function} [options.fetchFn] - The fetch function to use.
 * @returns {Object} - An object containing the icon content and a boolean indicating if the icon is currently being fetched.
 */
function useFetchIcon(icon, options) {
  const [iconContent, setIconContent] = (0, _react.useState)(null);
  const [isFetching, setIsFetching] = (0, _react.useState)(false);

  //
  // Fetches the icon and sets the icon content.
  // It will cache the fetched icon content to avoid fetching the same icon multiple times.
  //
  const fetchIcon = (0, _react.useCallback)(async function fetchIcon() {
    try {
      var _options$fetchFn;
      setIsFetching(true);
      const iconUrl = `${options.cdn}/${icon}.svg`;
      const response = await ((_options$fetchFn = options.fetchFn) !== null && _options$fetchFn !== void 0 ? _options$fetchFn : fetch)(iconUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const svgContent = await response.text();
      const parsedContent = _svgosm.svgosm.parse(svgContent);
      _svgosm.svgosm.set(icon, parsedContent);
      fetchCache.set(icon, parsedContent);
      return parsedContent;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching icon ${icon}:`, error);
      return null;
    } finally {
      setIsFetching(false);
    }
  }, [icon, options]);

  //
  // Fetches the icon content when the component mounts.
  // If the icon is already cached, it will set the icon content immediately.
  // Otherwise, it will fetch the icon and set the icon content when the fetch is complete.
  //
  (0, _react.useEffect)(function getIconOnMount() {
    if (_svgosm.svgosm.get(icon)) {
      setIconContent(_svgosm.svgosm.get(icon));
      return;
    }
    const cachedIconContent = fetchCache.get(icon);
    if (cachedIconContent instanceof Promise) {
      cachedIconContent.then(setIconContent);
      return;
    }
    if (cachedIconContent) {
      setIconContent(cachedIconContent);
      return;
    }
    const fetchPromise = fetchIcon();
    fetchCache.set(icon, fetchPromise);
    fetchPromise.then(setIconContent);
  }, [icon, fetchIcon]);
  return {
    iconContent,
    isFetching
  };
}
var _default = exports.default = useFetchIcon;